/**
 * Chat API Service
 * Following PRD Phase 3 API specifications - Connected to AccountsGenie Backend
 */

import { api, API_ENDPOINTS } from './api';
import { ChatApiResponse, ChatSession, Message } from '../types/chat';

// AccountsGenie Backend Chat API
// NOTE: Endpoints use /chat/... (not /api/chat/...) because:
// - VITE_API_URL includes /api (e.g., https://ageniee.com/api)
// - nginx strips /api/ prefix before forwarding to backend
// - So /chat/session → browser sends /api/chat/session → nginx sends /chat/session → backend matches
export const chatApi = {
  /**
   * Create a new chat session
   */
  createSession: async (title?: string): Promise<{ session: ChatSession; recent_messages?: Message[] }> => {
    const response = await api.post(API_ENDPOINTS.chat.session, {
      title: title || 'New Chat Session'
    });

    // Backend returns session data - handle multiple possible response structures
    let sessionData;

    if (response.data?.data) {
      // Wrapped response: { success: true, data: { id: ..., ... } }
      sessionData = response.data.data;
    } else if (response.data?.session) {
      // Session wrapper: { success: true, session: { id: ..., ... } }
      sessionData = response.data.session;
    } else if (response.data?.id || response.data?.session_id) {
      // Direct response: { id: ..., title: ..., ... }
      sessionData = response.data;
    } else {
      console.error('Unexpected session response format:', response.data);
      throw new Error('Invalid session response format from backend');
    }

    // Extract session ID with correct priority based on backend logs
    // Backend logs show session_id is the actual session identifier for connections
    const sessionId = sessionData?.session_id || sessionData?.id || sessionData?.sessionId;

    if (!sessionId) {
      console.error('No session ID found in response data:', sessionData);
      throw new Error('Session ID not found in backend response');
    }

    // Transform to expected format that matches ChatSession type
    return {
      session: {
        id: sessionId,
        title: sessionData.title || 'New Chat Session',
        messages: sessionData.recent_messages || [],
        createdAt: new Date(sessionData.created_at || new Date().toISOString()),
        updatedAt: new Date(sessionData.updated_at || new Date().toISOString())
      },
      recent_messages: sessionData.recent_messages || []
    };
  },

  /**
   * Send a message to the chat API
   */
  sendMessage: async (message: string, sessionId: string, stream: boolean = false): Promise<ChatApiResponse> => {
    const response = await api.post(API_ENDPOINTS.chat.message, {
      message: message,
      session_id: sessionId,
      stream: stream
    });

    // Backend returns the response directly or in a data field
    const backendResponse = response.data;

    // Handle both direct response and wrapped response formats
    const responseContent = backendResponse.response || backendResponse.data?.response || backendResponse.message || 'Processing your request...';
    const messageId = backendResponse.id || backendResponse.data?.id || backendResponse.message_id;
    const timestamp = backendResponse.timestamp || backendResponse.created_at || new Date().toISOString();

    return {
      response: responseContent,
      sessionId: sessionId,
      timestamp: timestamp,
      messageId: messageId
    };
  },

  /**
   * Get a chat session by ID with recent messages
   */
  getSession: async (sessionId: string): Promise<ChatSession> => {
    const response = await api.get(`/chat/session/${sessionId}`);

    // Backend returns { success, data, error } structure
    const backendSession = response.data.data || response.data.session || response.data;
    const recentMessages = response.data.recent_messages || [];

    // Transform backend session to frontend format
    // Use session_id as primary identifier for consistency with session creation
    return {
      id: backendSession.session_id || backendSession.id,
      title: backendSession.title || 'Chat Session',
      messages: recentMessages.map(transformBackendMessage),
      createdAt: new Date(backendSession.created_at || backendSession.createdAt),
      updatedAt: new Date(backendSession.updated_at || backendSession.updatedAt)
    };
  },

  /**
   * Get messages for a session
   */
  getMessages: async (sessionId: string, page: number = 1, pageSize: number = 50): Promise<Message[]> => {
    const response = await api.get(`/chat/session/${sessionId}/messages`, {
      params: { page, page_size: pageSize }
    });

    return response.data.messages.map(transformBackendMessage);
  },

  /**
   * Delete a chat session
   */
  deleteSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/chat/session/${sessionId}`);
  },

  /**
   * Get all chat sessions for the current user
   */
  getSessions: async (page: number = 1, pageSize: number = 20): Promise<ChatSession[]> => {
    const response = await api.get(API_ENDPOINTS.chat.sessions, {
      params: { page, page_size: pageSize }
    });

    return response.data.sessions.map((session: any) => ({
      id: session.id,
      title: session.title || 'Chat Session',
      messages: [],
      createdAt: new Date(session.created_at),
      updatedAt: new Date(session.updated_at)
    }));
  },

  /**
   * Get available tools for session
   */
  getTools: async (sessionId?: string) => {
    const response = await api.get(API_ENDPOINTS.chat.tools);

    // Extract data from response
    const data = response.data?.data || response.data;

    return {
      mcp_servers: data.mcp_servers || [],
      internal_tools: data.internal_tools || [],
      total_count: data.total_count || 0
    };
  },


  /**
   * Create WebSocket connection for streaming
   */
  createWebSocketConnection: (sessionId: string): WebSocket => {
    const baseUrl = import.meta.env.VITE_WS_URL;
    if (!baseUrl) {
      console.error('VITE_WS_URL environment variable is not set');
      // Create a dummy WebSocket that will fail to connect rather than throwing
      // This allows the app to load and show meaningful error messages
      return new WebSocket('ws://localhost:0/dummy');
    }
    const token = localStorage.getItem('auth_token');

    // Backend now accepts token via query parameter to work around browser WebSocket header limitations
    // CRITICAL: WebSocket URL must include /api/ prefix to match nginx routing
    const wsUrl = `${baseUrl}/api/chat/ws?session_id=${sessionId}${token ? `&token=${token}` : ''}`;

    return new WebSocket(wsUrl);
  },

  /**
   * Health check - using general health endpoint since /api/chat/health doesn't exist
   */
  healthCheck: async () => {
    const response = await api.get(API_ENDPOINTS.health);
    return response.data;
  },

  /**
   * Upload file to chat session
   */
  uploadFile: async (
    sessionId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{
    file_id: string;
    public_url: string;
    filename: string;
    content_type: string;
    file_size_bytes: number;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('session_id', sessionId);

    const response = await api.post(API_ENDPOINTS.chat.upload, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          onProgress(progress);
        }
      }
    });

    // Handle response format
    const data = response.data?.data || response.data;

    return {
      file_id: data.file_id,
      public_url: data.public_url,
      filename: data.filename,
      content_type: data.content_type,
      file_size_bytes: data.file_size_bytes
    };
  }
};

/**
 * Transform backend message format to frontend format
 */
function transformBackendMessage(backendMessage: any): Message {
  return {
    id: backendMessage.id,
    content: backendMessage.content,
    role: backendMessage.role,
    timestamp: new Date(backendMessage.created_at || backendMessage.timestamp || new Date()),
    status: mapBackendStatus(backendMessage.status)
  };
}

/**
 * Map backend message status to frontend status
 */
function mapBackendStatus(backendStatus: string): Message['status'] {
  switch (backendStatus) {
    case 'sending':
      return 'sending';
    case 'sent':
      return 'sent';
    case 'error':
      return 'error';
    default:
      return 'sent';
  }
}

// Mock API removed - use real API endpoints only
